import { cn } from "../../lib/utils.js";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog.js";
import { CATEGORY_STYLE, formatDayTitle } from "./todoStyles.js";
import { CATEGORY_ORDER, type TodoCategory } from "../../lib/calendarTodos.js";

// The Team calendar shows who is away as overlapping initial circles (the same
// look as the todo circles), coloured by leave type, so a busy day stays one
// row tall. The day dialog lists everyone, so nobody is hidden behind "+N".

export interface LeaveCircleRequest {
  employeeId: string;
  leaveType: string;
  halfDay: boolean;
  status?: string;
  employee: { firstName: string; lastName: string };
}

const KNOWN_TONE: Record<string, TodoCategory> = {
  ANNUAL: "work",
  SICK: "important",
  LOP: "reminder",
  COMP_OFF: "travel",
  BEREAVEMENT: "personal",
};

/** Stable colour per leave type: known types get a fixed one, others hash into the same palette. */
export function leaveTone(leaveType: string): TodoCategory {
  const known = KNOWN_TONE[leaveType];
  if (known) return known;
  let h = 0;
  for (const ch of leaveType) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CATEGORY_ORDER[h % CATEGORY_ORDER.length]!;
}

const initialsOf = (r: LeaveCircleRequest) =>
  `${r.employee.firstName[0] ?? ""}${r.employee.lastName[0] ?? ""}`.toUpperCase();
const isPending = (r: LeaveCircleRequest) => r.status === "PENDING";
/** The signed-in user's own leave is a rounded square; everyone else stays a circle. */
const shapeOf = (r: LeaveCircleRequest, selfId?: string) => (selfId && r.employeeId === selfId ? "rounded-lg" : "rounded-full");
const fullName = (r: LeaveCircleRequest) => `${r.employee.firstName} ${r.employee.lastName}`;
const typeLabel = (r: LeaveCircleRequest) => `${r.leaveType.replace(/_/g, " ")}${r.halfDay ? " (half day)" : ""}${isPending(r) ? " (pending)" : ""}`;

// Static z-index ladder so the first circle sits on top of the next.
const Z = ["z-[20]", "z-[19]", "z-[18]", "z-[17]", "z-[16]", "z-[15]"];

interface CirclesProps {
  requests: LeaveCircleRequest[];
  /** How many circles before the "+N" circle. */
  max: number;
  compact: boolean;
  /** Employee id of the signed-in user, whose leave is drawn as a rounded square. */
  selfId?: string | undefined;
  onOpen: (opener: HTMLElement) => void;
}

export function LeaveCircles({ requests, max, compact, selfId, onOpen }: CirclesProps) {
  if (requests.length === 0) return null;
  const shown = requests.slice(0, max);
  const extra = requests.length - shown.length;
  const size = compact ? "size-5 text-[9px]" : "size-6 text-[10px]";
  const overlap = compact ? "-ml-[9px]" : "-ml-2";

  return (
    <div className="mt-1.5 flex items-center">
      {shown.map((r, i) => (
        <button
          key={`${r.employeeId}-${i}`}
          type="button"
          title={`${fullName(r)} · ${typeLabel(r)}`}
          aria-label={`${fullName(r)}, ${typeLabel(r)}`}
          onClick={(e) => { e.stopPropagation(); onOpen(e.currentTarget); }}
          className={cn(
            "relative flex shrink-0 items-center justify-center p-0 font-bold leading-none shadow-[0_0_0_2px_var(--cb)] transition-transform duration-200 hover:z-30 hover:-translate-y-[3px] hover:scale-[1.12] focus-visible:z-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
            size,
            shapeOf(r, selfId),
            i > 0 && overlap,
            Z[i],
            CATEGORY_STYLE[leaveTone(r.leaveType)].circle,
            (r.halfDay || isPending(r)) && "opacity-60",
            isPending(r) && "border border-dashed border-current",
          )}
        >
          {initialsOf(r)}
        </button>
      ))}
      {extra > 0 && (
        <button
          type="button"
          aria-label={`${extra} more on leave`}
          onClick={(e) => { e.stopPropagation(); onOpen(e.currentTarget); }}
          className={cn(
            "relative z-[14] flex shrink-0 items-center justify-center rounded-full bg-surface-300 p-0 font-bold leading-none text-surface-900 shadow-[0_0_0_2px_var(--cb)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
            size,
            overlap,
          )}
        >
          +{extra}
        </button>
      )}
      <span className="ml-2 text-[10.5px] font-semibold text-surface-500">{requests.length} away</span>
    </div>
  );
}

interface DialogProps {
  dateKey: string | null;
  selfId?: string | undefined;
  requests: LeaveCircleRequest[];
  onClose: () => void;
}

export function LeaveDayDialog({ dateKey, selfId, requests, onClose }: DialogProps) {
  return (
    <Dialog open={dateKey !== null} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{dateKey ? formatDayTitle(dateKey) : ""}</DialogTitle>
          <DialogDescription>
            {requests.length} {requests.length === 1 ? "person" : "people"} on leave
          </DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col divide-y divide-surface-100">
          {requests.map((r, i) => (
            <li key={`${r.employeeId}-${i}`} className="flex items-center gap-3 py-2">
              <span className={cn("flex size-8 shrink-0 items-center justify-center text-[11px] font-bold", shapeOf(r, selfId), CATEGORY_STYLE[leaveTone(r.leaveType)].circle)}>
                {initialsOf(r)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold">{fullName(r)}</span>
              <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold", CATEGORY_STYLE[leaveTone(r.leaveType)].chip)}>
                {typeLabel(r)}
              </span>
            </li>
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  );
}
